import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { LogOut, Mail, MapPin, Menu, Phone, Search, Settings2, User, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/i18n'
import { siteConfig } from '@/config/site.config'
import { useOwner } from '@/hooks/useOwner'
import { useWatches } from '@/hooks/useWatches'
import { Container } from './Container'
import { LanguageSwitcher } from '@/components/patterns/LanguageSwitcher'
import { ThemeSwitcher, ThemeToggleButton } from '@/components/patterns/ThemeSwitcher'
import { SearchOverlay } from '@/components/search/SearchCommand'

/**
 * The header. Two states: it floats transparently over the dark hero on the
 * home page, and turns into a solid bar with a hairline the moment you scroll
 * or land on any other page.
 */
export function SiteHeader() {
  const { t, tx, tf } = useI18n()
  const { pathname } = useLocation()
  const { user, isOwner, signOut } = useOwner()
  const { collections } = useWatches()
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)

  const overHero = pathname === '/' && !scrolled

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Never leave the drawer hanging open across a navigation.
  useEffect(() => setOpen(false), [pathname])

  // ⌘K / Ctrl+K opens search from anywhere — the shortcut people already try.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = open ? 'hidden' : prev
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  return (
    <header
      data-upbo-id="site-header"
      data-upbo-type="header"
      data-upbo-label="Site header"
      className={cn(
        'fixed top-0 inset-x-0 z-40 transition-colors duration-500',
        overHero ? 'bg-transparent' : 'bg-bg/95 backdrop-blur-md border-b border-border',
      )}
    >
      <Container size="xl">
        <div
          className={cn(
            'flex items-center justify-between gap-2 sm:gap-6 transition-all duration-500',
            overHero ? 'h-20 sm:h-24' : 'h-16 sm:h-[68px]',
          )}
        >
          {/* ── Wordmark ─────────────────────────────────────────────────── */}
          <Link
            to="/"
            data-upbo-id="site-logo"
            data-upbo-label="Logo"
            className={cn(
              'flex flex-col leading-none min-w-0 shrink-0 max-w-[62%] sm:max-w-none transition-transform hover:opacity-90',
              overHero && 'text-white',
            )}
          >
            <span className="font-display text-[21px] sm:text-[26px] tracking-[0.16em] sm:tracking-[0.22em] pl-[0.16em] sm:pl-[0.22em] truncate">
              {siteConfig.name}
            </span>
            <span
              className={cn(
                'text-[8px] sm:text-[9px] tracking-[0.12em] sm:tracking-[0.24em] uppercase mt-0.5 sm:mt-1 truncate max-w-[170px] xs:max-w-[240px] sm:max-w-none',
                overHero ? 'text-white/60' : 'text-text-faint',
              )}
            >
              {tx(siteConfig.tagline)}
            </span>
          </Link>

          {/* ── Desktop navigation ───────────────────────────────────────── */}
          <nav className="hidden lg:flex items-center gap-8 xl:gap-9" aria-label="Main">
            {siteConfig.nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  cn(
                    'link-underline text-[11px] font-medium uppercase tracking-[0.18em] transition-colors',
                    overHero
                      ? isActive
                        ? 'text-white'
                        : 'text-white/70 hover:text-white'
                      : isActive
                        ? 'text-text'
                        : 'text-text-muted hover:text-text',
                  )
                }
              >
                {t(`nav.${item.key}`)}
              </NavLink>
            ))}
          </nav>

          {/* ── Right-hand controls ──────────────────────────────────────── */}
          <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label={t('search.label')}
              title={t('search.openHint')}
              data-upbo-id="header-search"
              data-upbo-label="Search"
              className={cn(
                'h-9 w-9 sm:h-10 sm:w-10 grid place-items-center rounded-squircle transition-colors',
                overHero ? 'text-white hover:bg-white/10' : 'text-text-muted hover:text-text hover:bg-surface-2',
              )}
            >
              <Search size={17} strokeWidth={1.5} />
            </button>

            <div className="hidden md:flex items-center gap-1.5">
              <LanguageSwitcher onDark={overHero} />
              <ThemeSwitcher onDark={overHero} />
            </div>

            {isOwner ? (
              <Link
                to="/studio"
                className={cn(
                  'hidden sm:inline-flex items-center gap-2 h-9 px-3.5 rounded-squircle text-[11px] font-medium uppercase tracking-[0.14em] transition-colors',
                  overHero ? 'text-white hover:bg-white/10' : 'text-text-muted hover:text-text hover:bg-surface-2',
                )}
              >
                <Settings2 size={15} strokeWidth={1.5} />
                {t('nav.studio')}
              </Link>
            ) : (
              <Link
                to="/signin"
                className={cn(
                  'hidden sm:inline-flex items-center gap-2 h-9 px-3.5 rounded-squircle text-[11px] font-medium uppercase tracking-[0.14em] transition-colors',
                  overHero ? 'text-white hover:bg-white/10' : 'text-text-muted hover:text-text hover:bg-surface-2',
                )}
              >
                <User size={15} strokeWidth={1.5} />
                {t('nav.signIn')}
              </Link>
            )}

            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label={t('nav.openMenu')}
              className={cn(
                'lg:hidden h-9 w-9 sm:h-10 sm:w-10 grid place-items-center rounded-squircle transition-colors',
                overHero ? 'text-white hover:bg-white/10' : 'text-text hover:bg-surface-2',
              )}
            >
              <Menu size={20} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </Container>

      {/* ── Mobile / tablet drawer ─────────────────────────────────────────── */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-xs animate-fade-in" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-[360px] sm:max-w-[400px] h-full bg-bg border-l border-border flex flex-col z-10 shadow-float animate-slide-left">
            {/* Header ngăn kéo */}
            <div className="flex items-center justify-between h-16 sm:h-[68px] px-5 sm:px-6 border-b border-border shrink-0 bg-surface/50">
              <div className="flex flex-col min-w-0">
                <span className="font-display text-lg sm:text-xl tracking-[0.2em]">{siteConfig.name}</span>
                <span className="text-[8px] tracking-[0.16em] uppercase text-text-faint truncate">
                  {tx(siteConfig.tagline)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t('nav.close')}
                className="h-9 w-9 grid place-items-center rounded-squircle text-text-muted hover:text-text hover:bg-surface-2 transition-colors shrink-0"
              >
                <X size={19} strokeWidth={1.5} />
              </button>
            </div>

            {/* Thân ngăn kéo cuộn mượt */}
            <nav className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 flex flex-col gap-6 no-scrollbar" aria-label="Mobile">
              {/* Nút tìm kiếm nhanh */}
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  setSearchOpen(true)
                }}
                className="flex items-center justify-between h-11 px-3.5 rounded-squircle border border-border bg-surface text-text-muted hover:text-text hover:border-text-faint transition-all"
              >
                <div className="flex items-center gap-2.5">
                  <Search size={16} strokeWidth={1.5} className="text-primary" />
                  <span className="text-xs">{t('search.placeholder')}</span>
                </div>
                <span className="text-[10px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded bg-surface-2 text-text-faint">
                  ⌘K
                </span>
              </button>

              {/* Danh sách trang chính */}
              <div className="flex flex-col gap-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-faint px-1">
                  {t('nav.menu') || 'Điều hướng'}
                </span>
                <div className="flex flex-col">
                  {siteConfig.nav.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === '/'}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center justify-between py-2.5 px-2 rounded-squircle font-display text-xl sm:text-2xl transition-colors',
                          isActive ? 'text-primary bg-primary/5 font-semibold' : 'text-text hover:text-primary hover:bg-surface-2',
                        )
                      }
                    >
                      <span>{t(`nav.${item.key}`)}</span>
                    </NavLink>
                  ))}
                </div>
              </div>

              {/* Bộ sưu tập tuyển chọn (Collections Shortcut) */}
              {collections && collections.length > 0 && (
                <div className="flex flex-col gap-2.5 pt-2 border-t border-border">
                  <div className="flex items-center justify-between px-1">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-faint">
                      {t('nav.collection')}
                    </span>
                    <Link
                      to="/collection"
                      onClick={() => setOpen(false)}
                      className="text-[10.5px] tracking-wider uppercase text-primary hover:underline font-medium"
                    >
                      {t('common.viewAll')}
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {collections.map((c) => (
                      <Link
                        key={c.id}
                        to={`/collection?collection=${c.slug}`}
                        onClick={() => setOpen(false)}
                        className="flex flex-col gap-0.5 p-2.5 rounded-squircle border border-border/80 bg-surface hover:border-primary/50 hover:bg-surface-2 transition-all group"
                      >
                        <span className="text-xs font-bold text-text group-hover:text-primary transition-colors">
                          {tf(c, 'name')}
                        </span>
                        <span className="text-[10px] text-text-faint line-clamp-1">
                          {tf(c, 'tagline')}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Thông tin Salon & Liên hệ trực tiếp */}
              <div className="flex flex-col gap-2.5 pt-2 border-t border-border">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-faint px-1">
                  {t('contact.title') || 'Salon & Liên hệ'}
                </span>
                <div className="flex flex-col gap-2 text-xs text-text-muted bg-surface p-3 rounded-squircle border border-border/70">
                  <div className="flex flex-col gap-0.5">
                    <span className="font-semibold text-text">{siteConfig.salons[0]?.city ? tx(siteConfig.salons[0].city) : 'Hà Nội'}:</span>
                    <span className="text-[11px] text-text-muted">{siteConfig.salons[0]?.address ? tx(siteConfig.salons[0].address) : '18 Lý Thái Tổ, Hoàn Kiếm'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-border/50 text-[11px]">
                    <a href={`tel:${siteConfig.contact.phone}`} className="text-primary hover:underline font-medium flex items-center gap-1">
                      <Phone size={12} />
                      <span>{siteConfig.contact.phone}</span>
                    </a>
                    <a href={`mailto:${siteConfig.contact.email}`} className="text-text-muted hover:text-text truncate flex items-center gap-1">
                      <Mail size={12} />
                      <span>{siteConfig.contact.email}</span>
                    </a>
                  </div>
                </div>
              </div>

              {/* Tài khoản & Quản trị */}
              <div className="flex flex-col gap-2 pt-2 border-t border-border">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-faint px-1">
                  {t('nav.studio') || 'Tài khoản'}
                </span>
                {isOwner ? (
                  <div className="flex flex-col gap-1.5">
                    <Link
                      to="/studio"
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-2.5 h-10 px-3 rounded-squircle bg-surface-2 text-xs font-semibold text-text hover:bg-primary hover:text-white transition-colors"
                    >
                      <Settings2 size={15} strokeWidth={1.5} />
                      <span>{t('nav.studio')}</span>
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        void signOut()
                        setOpen(false)
                      }}
                      className="flex items-center gap-2.5 h-10 px-3 rounded-squircle text-xs font-semibold text-error hover:bg-error-soft transition-colors text-left"
                    >
                      <LogOut size={15} strokeWidth={1.5} />
                      <span>{t('nav.signOut')}</span>
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/signin"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2.5 h-10 px-3 rounded-squircle bg-surface-2 text-xs font-semibold text-text hover:bg-primary hover:text-white transition-colors"
                  >
                    <User size={15} strokeWidth={1.5} />
                    <span>{user ? t('nav.studio') : t('nav.signIn')}</span>
                  </Link>
                )}
              </div>
            </nav>

            {/* Chân ngăn kéo: Đổi ngôn ngữ & Giao diện */}
            <div className="px-5 sm:px-6 py-4 border-t border-border bg-surface/60 flex items-center justify-between gap-3 shrink-0">
              <LanguageSwitcher inline />
              <ThemeToggleButton />
            </div>
          </div>
        </div>
      )}

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  )
}
